import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { forEachTelemetryIntegration } from '@src/telemetry/telemetry-registry'

import { TelemetryEventDataMap } from './telemetry-events.types'

const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)

const generateUniqueEventId = () => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-optional-chain
  if (crypto && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return (S4() + S4() + '-' + S4() + '-4' + S4().substr(0, 3) + '-' + S4() + '-' + S4() + S4() + S4()).toLowerCase()
}

// sss

const getEnhancedProperties = (properties: unknown) => {
  return {
    eventId: generateUniqueEventId(),
    ...(properties as NonNullable<unknown>),
  }
}

export function trackEvent<T extends keyof TelemetryEventDataMap>(event: T, data: unknown) {
  try {
    const enhancedProperties = getEnhancedProperties(data) as TelemetryEventDataMap[T]
    forEachTelemetryIntegration((_integrationKey, integration) => {
      if (integration.events && integration.events[event]) {
        const result = integration.events[event](enhancedProperties)
        if (result && typeof result.then === 'function') {
          result.catch(() => {
            // ignore errors
          })
        }
      }
    })
  } catch (e) {
    captureException(e, {
      module: MODULES.EVENT_TRACKING,
      feature: FEATURES.TRACK_EVENT,
    })
  }
}

export function trackGAEvent<T extends keyof TelemetryEventDataMap>(
  event: T,
  data: unknown,
  shouldGenerateEventId = true,
) {
  try {
    const enhancedProperties = shouldGenerateEventId
      ? (getEnhancedProperties(data) as TelemetryEventDataMap[T])
      : (data as TelemetryEventDataMap[T])
    forEachTelemetryIntegration(async (_integrationKey, integration) => {
      if (_integrationKey === 'googleAnalytics' && integration.events && integration.events[event]) {
        await integration.events[event](enhancedProperties)
      }
    })
  } catch (e) {
    captureException(e, {
      module: MODULES.EVENT_TRACKING,
      feature: FEATURES.TRACK_GA,
    })
  }
}

export function trackInternalEvent<T extends keyof TelemetryEventDataMap>(event: T, data: unknown) {
  try {
    const enhancedProperties = getEnhancedProperties(data) as TelemetryEventDataMap[T]
    forEachTelemetryIntegration(async (_integrationKey, integration) => {
      if (_integrationKey === 'internalAnalytics' && integration.events && integration.events[event]) {
        await integration.events[event](enhancedProperties)
      }
    })
  } catch (e) {
    captureException(e, {
      module: MODULES.EVENT_TRACKING,
      feature: FEATURES.TRACK_INTERNAL_ANALYTICS,
    })
  }
}

export function trackClickEvent<T extends keyof TelemetryEventDataMap>(event: T, data: Record<string, unknown>) {
  const { href: pageUrl, pathname } = window.location
  if (data.trackGA) {
    trackGAEvent(event, {
      ...data,
      pageUrl,
      pathname,
    })
  }
  if (data.trackInternal) {
    trackInternalEvent(event, {
      ...data,
      pageUrl,
      pathname,
    })
  }
}
